import React, { createContext, useContext, useState, useCallback } from 'react';

const VariableContext = createContext();

export const VariableProvider = ({ children }) => {
  const [variableDict, setVariableDict] = useState({});
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Function to update a variable and trigger updates
  const updateVariable = useCallback((key, value) => {
    setVariableDict(prev => ({
      ...prev,
      [key]: { ...prev[key], value }
    }));
    // Increment trigger to notify all subscribers
    setUpdateTrigger(prev => prev + 1);
  }, []);

  // Function to set multiple variables at once
  const updateVariableDict = useCallback((updates) => {
    setVariableDict(prev => ({
      ...prev,
      ...updates
    }));
    setUpdateTrigger(prev => prev + 1);
  }, []);

  // Function to initialize a variable
  const initializeVariable = useCallback((key, initialValue = '', fieldId = null) => {
    setVariableDict(prev => {
      if (prev[key]) return prev; // Don't reinitialize
      return {
        ...prev,
        [key]: { value: initialValue, fieldId }
      };
    });
  }, []);

  // Function to rename a variable
  const renameVariable = useCallback((oldKey, newKey) => {
    setVariableDict(prev => {
      // Check if newKey already exists to avoid conflicts
      if (prev[newKey]) {
        console.warn(`Variable "${newKey}" already exists. Rename aborted.`);
        return prev;
      }
      
      const { [oldKey]: value, ...rest } = prev;
      
      // If oldKey doesn't exist, return unchanged
      if (!value) {
        console.warn(`Variable "${oldKey}" does not exist. Rename aborted.`);
        return prev;
      }
      
      return {
        ...rest,
        [newKey]: value
      };
    });
    setUpdateTrigger(prev => prev + 1);
  }, []);

  return (
    <VariableContext.Provider 
      value={{ 
        variableDict, 
        setVariableDict,
        updateVariable,
        updateVariableDict,
        initializeVariable,
        renameVariable,
        updateTrigger // Components can listen to this
      }}
    >
      {children}
    </VariableContext.Provider>
  );
};

export const useVariables = () => {
  const context = useContext(VariableContext);
  if (!context) {
    throw new Error('useVariables must be used within a VariableProvider');
  }
  return context;
};